import { Flex, Text, Loader, Divider, Modal, Button } from '@mantine/core';
import { useParams, useNavigate } from 'react-router-dom';
import colors from '../../utils/colors';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { staggerContainer, fadeInFromTop, fadeInFromLeft, fadeInFromBottom } from '../../utils/animations';
import { useState, useEffect } from 'react';
import { useGroupClassDetail, useGroupClassSignup } from '../../hooks/useGroupClasses';
import {
  IconCalendarEvent, IconClock, IconUsers, IconCheck, IconArrowLeft,
  IconRepeat, IconUser, IconAlertTriangle, IconBan, IconLock, IconInfoCircle
} from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';

const formatDatePolish = (date: Date): string => {
  const day = date.getDate();
  const month = date.toLocaleDateString('pl-PL', { month: 'long' });
  const year = date.getFullYear();
  const weekday = date.toLocaleDateString('pl-PL', { weekday: 'long' });
  return `${day} ${month} ${year} (${weekday})`;
};

const ClassDetail: React.FC = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const { translate } = useLanguage();
  const { data, isLoading, refetch } = useGroupClassDetail(Number(instanceId));
  const { signup, signout } = useGroupClassSignup();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showRestrictedModal, setShowRestrictedModal] = useState(false);
  const [showOnePerDayModal, setShowOnePerDayModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isLoading && data) {
      setTimeout(() => setShouldAnimate(true), 100);
    }
  }, [isLoading, data]);

  const handleSignUp = async () => {
    if (!data) return;
    setActionLoading(true);
    try {
      const result = await signup.mutateAsync(data.instanceId);

      if (result.restricted) {
        // 2+ absences — not signed up, show restricted modal
        setShowRestrictedModal(true);
      } else if (result.warning) {
        // 1 absence — signed up but show warning modal
        showNotification({
          title: translate('success'),
          message: translate('signUpSuccess'),
          color: 'green',
        });
        refetch();
        setShowWarningModal(true);
      } else {
        // No absences — normal signup
        showNotification({
          title: translate('success'),
          message: translate('signUpSuccess'),
          color: 'green',
        });
        refetch();
      }
    } catch (error: any) {
      const isOnePerDay = error.response?.data?.onePerDayRestricted;
      if (isOnePerDay) {
        setShowOnePerDayModal(true);
      } else {
        showNotification({
          title: translate('error'),
          message: translate('signUpError'),
          color: 'red',
        });
      }
    }
    setActionLoading(false);
  };

  const handleSignOut = async () => {
    if (!data) return;
    setActionLoading(true);
    try {
      await signout.mutateAsync(data.instanceId);
      showNotification({
        title: translate('success'),
        message: translate('signOutSuccess'),
        color: 'green',
      });
      refetch();
    } catch (error: any) {
      const isResignClosed = error.response?.data?.resignWindowClosed;
      showNotification({
        title: translate('error'),
        message: isResignClosed ? translate('resignWindowClosed') : translate('signOutError'),
        color: 'red',
      });
      if (isResignClosed) refetch();
    }
    setActionLoading(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader color={colors.custom36p} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4 mt-5">
        <Text c={colors.dark} ta="center">Not found</Text>
      </div>
    );
  }

  const date = new Date(data.date);
  const spotsLeft = data.maxMembers - data.signupCount;
  const isFull = spotsLeft <= 0;

  const resignWindowHours = data.resignWindowHours ?? 0;
  const resignDeadline = resignWindowHours > 0
    ? new Date(date.getTime() - resignWindowHours * 60 * 60 * 1000)
    : null;
  const isResignWindowClosed = resignDeadline ? new Date() >= resignDeadline : false;

  return (
    <motion.div
      className="p-4 mt-5 pb-24"
      variants={staggerContainer}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
      exit={{ opacity: 0, transition: { duration: 0.3 } }}
    >
      {/* Back button - centered */}
      <motion.div variants={fadeInFromTop}>
        <div
          className="flex items-center justify-center gap-2 mb-4 cursor-pointer"
          onClick={() => navigate('/zajecia')}
        >
          <IconArrowLeft size={18} color={colors.custom36s} />
          <Text c={colors.custom36s} fw={600} size="sm">
            {translate('goBack')}
          </Text>
        </div>
      </motion.div>

      {/* Unified info card: title, badge, date, info, description */}
      <motion.div variants={fadeInFromLeft}>
        <div
          className="rounded-xl overflow-hidden mb-4"
          style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
        >
          <div
            className="p-4"
            style={{ background: 'white' }}
          >
            <Flex direction="column" gap={12}>
              {/* Title */}
              <Flex align="center" gap={8}>
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: data.color, flexShrink: 0 }}
                />
                <span className="font-semibold text-custom36p text-lg">
                  {data.title}
                </span>
              </Flex>

              {data.host && (
                <Flex align="center" gap={6}>
                  <IconUser size={16} color={colors.primary} />
                  <Text size="sm" fw={500} c={colors.custom36s}>
                    {data.host}
                  </Text>
                </Flex>
              )}

              <Divider color={colors.custom36p} opacity={0.2} />

              {/* Date & time + signed-up badge */}
              <Flex align="center" gap={10}>
                <IconCalendarEvent size={20} color={colors.primary} />
                <Flex direction="column">
                  <Text size="md" fw={600} c={colors.custom36s}>
                    {formatDatePolish(date)}
                  </Text>
                  <Flex align="center" gap={8}>
                    <Text size="md" fw={600} c={colors.custom36s}>
                      {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Text>
                    {data.isSignedUp && (
                      <div
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded-full font-bold"
                        style={{
                          backgroundColor: `${colors.green}20`,
                          color: colors.green,
                          fontSize: 10,
                        }}
                      >
                        <IconCheck size={10} />
                        {translate('signedUp')}
                      </div>
                    )}
                  </Flex>
                </Flex>
              </Flex>

              <Divider color={colors.custom36p} opacity={0.2} />

              {/* Duration & participants */}
              <Flex gap={20}>
                <Flex align="center" gap={8}>
                  <IconClock size={20} color={colors.primary} />
                  <Flex direction="column">
                    <Text size="xs" c={colors.dark}>{translate('duration')}</Text>
                    <Text size="sm" fw={600} c={colors.custom36s}>{data.durationMinutes} min</Text>
                  </Flex>
                </Flex>

                <Flex align="center" gap={8}>
                  <IconUsers size={20} color={colors.primary} />
                  <Flex direction="column">
                    <Text size="xs" c={colors.dark}>{translate('participants')}</Text>
                    <Flex align="baseline" gap={4}>
                      <Text size="sm" fw={600} c={isFull ? colors.red : colors.custom36s}>
                        {data.signupCount}/{data.maxMembers}
                      </Text>
                      <Text size="xs" fw={600} c={isFull ? colors.red : colors.custom36s}>
                        (min. {data.minMembers})
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>

              {data.isCyclical && (
                <Flex align="center" gap={8}>
                  <IconRepeat size={20} color={colors.primary} />
                  <Flex direction="column">
                    <Text size="xs" c={colors.dark}>{translate('cyclicalClass')}</Text>
                  </Flex>
                </Flex>
              )}

              {resignWindowHours > 0 && (data.isSignedUp || isResignWindowClosed) && (
                <>
                  <Divider color={colors.custom36p} opacity={0.2} />
                  <Flex align="center" gap={8}>
                    <IconLock size={20} color={colors.orange} />
                    <Flex direction="column">
                      <Text size="xs" fw={500} c={colors.orange}>
                        {data.isSignedUp
                          ? (isResignWindowClosed
                              ? translate('resignWindowClosed')
                              : translate('resignWindowInfo', {
                                  time: resignDeadline!.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                                  date: resignDeadline!.toLocaleDateString(),
                                })
                            )
                          : translate('resignWindowCommitWarning')
                        }
                      </Text>
                    </Flex>
                  </Flex>
                </>
              )}
            </Flex>
          </div>

          {/* Description section - connected, lighter bg */}
          {data.description && (
            <div
              className="px-4 pb-4"
              style={{ background: '#f8f9fa', paddingTop: '0.7rem' }}
            >
              <Text size="xs" fw={500} c={colors.dark} mb={4}>
                {translate('classDescription')}
              </Text>
              <Text size="xs" fw={400} c={colors.custom36s} style={{ whiteSpace: 'pre-wrap' }}>
                {data.description}
              </Text>
            </div>
          )}
        </div>
      </motion.div>

      {/* Action button */}
      <motion.div variants={fadeInFromBottom}>
        <div className="flex justify-center mt-4 mb-6">
          {data.isSignedUp ? (
            isResignWindowClosed ? (
              <motion.button
                className="uppercase text-white font-bold rounded-xl text-sm px-8 py-3 flex items-center justify-center gap-2 shadow-lg bg-gray-400 cursor-not-allowed"
                disabled
              >
                <IconLock size={16} />
                {translate('resignWindowClosedShort')}
              </motion.button>
            ) : (
            <motion.button
              className="uppercase text-white bg-gradient-to-br from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 shadow-lg font-bold rounded-xl text-sm px-8 py-3 flex items-center justify-center gap-2"
              onClick={() => setShowSignOutModal(true)}
              disabled={actionLoading}
              whileTap={{ scale: 0.95 }}
            >
              {actionLoading ? <Loader size="xs" color="white" /> : translate('signOut')}
            </motion.button>
            )
          ) : (
            <motion.button
              className={`uppercase text-white font-bold rounded-xl text-sm px-8 py-3 flex items-center justify-center gap-2 shadow-lg ${
                isFull
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-br from-custom36p via-custom36s/60 to-custom36s hover:from-custom36s hover:to-custom36p focus:ring-4 focus:outline-none focus:ring-custom36p shadow-custom36p/50'
              }`}
              onClick={handleSignUp}
              disabled={actionLoading || isFull}
              whileTap={isFull ? undefined : { scale: 0.95 }}
            >
              {actionLoading ? (
                <Loader size="xs" color="white" />
              ) : isFull ? (
                translate('classFull')
              ) : (
                translate('signUp')
              )}
            </motion.button>
          )}
        </div>
      </motion.div>

      {/* Participants list */}
      <motion.div variants={fadeInFromBottom}>
        <Text fw={600} size="md" c={colors.custom36s} mb="sm">
          {translate('participants')} ({data.signupCount}/{data.maxMembers})
        </Text>

        {data.participants.length === 0 ? (
          <Text c={colors.dark} size="sm" ta="center" p="md">
            {translate('noParticipants')}
          </Text>
        ) : (
          <>
            <Flex direction="column" gap={6}>
              {data.participants.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 p-3 rounded-lg"
                  style={{
                    backgroundColor: colors.customGray,
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: colors.custom36p }}
                  >
                    {p.name.charAt(0)}{p.surnameInitial}
                  </div>
                  <Text size="sm" fw={500} c={colors.custom36s}>
                    {p.name} {p.surnameInitial}.
                  </Text>
                </div>
              ))}
            </Flex>
            {data.signupCount < data.minMembers && (
              <Text size="sm" c={colors.dark} ta="center" mt="xs">
                {translate('missingAttendees', { count: data.minMembers - data.signupCount })}
              </Text>
            )}
          </>
        )}
      </motion.div>

      {/* Sign out confirmation modal */}
      <Modal
        opened={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
        title={<Text fw={700} c={colors.custom36s}>{translate('signOut')}</Text>}
        radius="lg"
        centered
      >
        <Flex direction="column" gap={15}>
          <Text size="sm" c={colors.dark}>
            {translate('confirmSignOut')}
          </Text>
          <Flex gap={10} justify="flex-end">
            <Button variant="outline" color="gray" radius="md" onClick={() => setShowSignOutModal(false)}>
              {translate('cancel')}
            </Button>
            <Button
              color="red"
              radius="md"
              loading={actionLoading}
              onClick={async () => {
                await handleSignOut();
                setShowSignOutModal(false);
              }}
            >
              {translate('signOut')}
            </Button>
          </Flex>
        </Flex>
      </Modal>

      {/* Absence warning modal (1 absence — signed up but warned) */}
      <Modal
        opened={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        title={
          <Flex align="center" gap={8}>
            <IconAlertTriangle size={22} color={colors.orange} />
            <Text fw={700} c={colors.orange}>{translate('absenceWarningTitle')}</Text>
          </Flex>
        }
        radius="lg"
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      >
        <Flex direction="column" gap={15}>
          <Text size="sm" c={colors.custom36s} style={{ lineHeight: 1.6 }}>
            {translate('absenceWarningMessage')}
          </Text>
          <Flex justify="center">
            <Button
              color={colors.primary}
              radius="md"
              size="md"
              onClick={() => setShowWarningModal(false)}
            >
              {translate('understood')}
            </Button>
          </Flex>
        </Flex>
      </Modal>

      {/* Absence restricted modal (2+ absences — not signed up) */}
      <Modal
        opened={showRestrictedModal}
        onClose={() => setShowRestrictedModal(false)}
        title={
          <Flex align="center" gap={8}>
            <IconBan size={22} color={colors.red} />
            <Text fw={700} c={colors.red}>{translate('absenceRestrictedTitle')}</Text>
          </Flex>
        }
        radius="lg"
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      >
        <Flex direction="column" gap={15}>
          <Text size="sm" c={colors.custom36s} style={{ lineHeight: 1.6 }}>
            {translate('absenceRestrictedMessage')}
          </Text>
          <Flex justify="center">
            <Button
              color={colors.primary}
              radius="md"
              size="md"
              onClick={() => setShowRestrictedModal(false)}
            >
              {translate('understood')}
            </Button>
          </Flex>
        </Flex>
      </Modal>

      {/* One class per day restriction modal */}
      <Modal
        opened={showOnePerDayModal}
        onClose={() => setShowOnePerDayModal(false)}
        title={
          <Flex align="center" gap={8}>
            <IconAlertTriangle size={22} color={colors.orange} />
            <Text fw={700} c={colors.orange}>{translate('onePerDayTitle')}</Text>
          </Flex>
        }
        radius="lg"
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      >
        <Flex direction="column" gap={15}>
          <Text size="sm" c={colors.custom36s} style={{ lineHeight: 1.6 }}>
            {translate('onePerDayMessage')}
          </Text>
          <Flex justify="center">
            <Button
              color={colors.primary}
              radius="md"
              size="md"
              onClick={() => setShowOnePerDayModal(false)}
            >
              {translate('understood')}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </motion.div>
  );
};

export default ClassDetail;
